import React, { useState } from 'react'
import { useForm } from 'react-hook-form'

import BaseNumberCard from '../user/views/profile/BaseNumberCard'
import MainModal from './common/MainModal'

import helpers from '../../commonHelpers/helpers'
import useToasterHelper from '../../helpers/ToasterHelper'

import { UserModel } from '../../models/users/user.model'

import { IHorseData } from '../../models/horse/horse.interface'
import { IReqFieldType } from '../../pages/competitor/clinicAndOther/Tabs/components/ProfileFields'

import FirestoreService from '../../services/firestoreService'
import FirebaseStorageService from '../../services/storageService'

import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectHorses, setHorses } from '../../store/horses/horseSlice'
import { selectProfileData, setProfileDetails } from '../../store/user/userSlice'

import { CONST } from '../../const/const'
import { MESSAGES_CONST } from '../../const/messages-const'
import { MODAL_CONSTS } from '../../const/modal-const'

interface IProps {
  show: boolean
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {
    fieldType: IReqFieldType
    fieldName: string
    fieldKey: string
    numberLogo: string
    userData?: UserModel
    horseData?: IHorseData
    isHorse?: boolean
    onSave?: ({
      updatedUserData,
      updateHorseData,
    }: {
      updatedUserData?: { [key: string]: string }
      updateHorseData?: { [key: string]: string }
    }) => void
  }
}

export const UpdateUserNumberModal: React.FC<IProps> = ({ dataToPassOn, handleModal }) => {
  const dispatch = useAppDispatch()
  const myHorses = useAppSelector(selectHorses)

  const { userData, horseData, isHorse, fieldKey, fieldName, fieldType, numberLogo, onSave } =
    dataToPassOn

  const toastFunctions = useToasterHelper()
  const [loading, setLoading] = useState(false)
  const cutValue = fieldKey.replace('Number', '')
  const profileDetails = useAppSelector(selectProfileData)

  function getHorseProperty(horse: IHorseData | undefined, propertyName: string): any {
    if (!horse) return ''

    // type guard to check if property exists on horse object
    if (Object.prototype.hasOwnProperty.call(horse, propertyName)) {
      return (horse as any)[propertyName]
    }

    return ''
  }

  function getUserProperty(user: UserModel | undefined, propertyName: string): any {
    if (!user) return {}

    //  type guard check if property exists on user object
    if (Object.prototype.hasOwnProperty.call(user, propertyName)) {
      return (user as any)[propertyName]
    }

    return {}
  }

  const getDefaultValues = () => {
    // horse numbers
    if (isHorse && horseData) {
      return {
        // number val (e.g., horseArhaNumber)
        [fieldKey]: getHorseProperty(horseData, fieldKey),
        // expiration date (e.g., horseArhaExpiration)
        [`${cutValue}Expiration`]: getHorseProperty(horseData, `${cutValue}Expiration`),
      }
    }
    //  user numbers
    else if (userData) {
      const userFieldValue = getUserProperty(userData, fieldKey)
      return {
        [fieldKey]: {
          documentNumber: userFieldValue.documentNumber || '',
          endDate: userFieldValue.endDate || '',
        },
      }
    }
    return {}
  }

  const getInitialImageUrl = (): string => {
    if (isHorse && horseData) {
      const imageKey = fieldKey.replace('Number', 'Image')
      return (horseData as any)[imageKey] || ''
    } else if (userData) {
      const userFieldValue = (userData as any)[fieldKey]
      return userFieldValue?.image || ''
    }
    return ''
  }

  const { control, register, handleSubmit } = useForm({
    mode: 'onChange',
    defaultValues: getDefaultValues(),
  })

  const [imageUrl, setImageUrl] = useState<string>(getInitialImageUrl())

  const handleImageUpload = async (file: File): Promise<void> => {
    if (!file) {
      toastFunctions.info({ message: MESSAGES_CONST.NO_FILE_SELECTED })
      return
    }

    try {
      setLoading(true)
      const PATH = isHorse
        ? CONST.DATA.STORAGE.HORSE.NUMBERS_URL.PREFIX
        : CONST.DATA.STORAGE.USERS.NUMBERS_URL.PREFIX

      const id = isHorse ? horseData?.id : userData?.id
      if (!id) {
        throw new Error('Missing ID for upload')
      }

      const downloadUrl = (await FirebaseStorageService.uploadFile(
        file,
        `${PATH}/${id}/${new Date().getTime()}`
      )) as string

      setImageUrl(downloadUrl)
      toastFunctions.success({ message: MESSAGES_CONST.PIC_UPLOADED })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      helpers.logger({
        isError: true,
        message: error,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImageRemove = async (): Promise<void> => {
    if (!imageUrl) return

    try {
      setLoading(true)
      await FirebaseStorageService.deleteFile(imageUrl)

      setImageUrl('')
      toastFunctions.success({ message: MESSAGES_CONST.IMAGE_REMOVED })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  const updateHorse = async (dataForm: any): Promise<void> => {
    let isValid = true
    for (const val of Object.values(dataForm)) {
      if (!val) {
        isValid = false
        break
      }
    }
    if (!isValid) {
      toastFunctions.error({ message: 'All fields are required' })
      return
    }

    const updateHorseData: Record<string, any> = {
      [fieldKey]: dataForm[fieldKey],
      [cutValue + 'Expiration']: `${dataForm[cutValue + 'Expiration']}`,
    }

    if (imageUrl) {
      updateHorseData[fieldKey.replace('Number', 'Image')] = imageUrl
    }

    if (!horseData) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      return
    }

    setLoading(true)

    try {
      await FirestoreService.updateItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.HORSES.NAME,
        horseData.id,
        updateHorseData
      )

      const updated = myHorses.map((horse) => {
        if (horse.id === horseData?.id) {
          return {
            ...horse,
            ...updateHorseData,
          }
        } else {
          return horse
        }
      })

      dispatch(setHorses(updated))
      if (onSave) onSave({ updateHorseData: updateHorseData })
      return handleModal(false, MODAL_CONSTS.UPDATE_USER_NUMBER)
    } catch (e) {
      console.log(e)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  const updateUser = async (dataForm: any): Promise<void> => {
    const obj = dataForm[fieldKey]
    if (!obj.documentNumber) {
      toastFunctions.error({ message: 'Field Number is required' })
      return
    }
    if (!obj.endDate) {
      toastFunctions.error({ message: 'Field Expires date is required' })
      return
    }

    setLoading(true)
    const updateData: Record<string, any> = {
      [fieldKey]: {
        ...obj,
        documentNumber: obj.documentNumber,
        endDate: `${obj.endDate}`,
        image: imageUrl,
      },
    }

    if (!userData) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      return
    }

    try {
      await FirestoreService.updateItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.USERS.NAME,
        userData.id,
        updateData
      )

      if (profileDetails.id === userData.id) {
        dispatch(setProfileDetails({ ...profileDetails, ...updateData }))
      }

      if (onSave) onSave({ updatedUserData: updateData })

      return handleModal(false, MODAL_CONSTS.UPDATE_USER_NUMBER)
    } catch (e) {
      console.log(e)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      return
    } finally {
      setLoading(false)
    }
  }

  return (
    <MainModal
      show={true}
      setHeightAsPerContent={true}
      type="UPDATE_USER_NUMBER"
      overlayClassName="z-[49]"
      className="!overflow-visible max-h-[calc(100dvh_-46px)] z-[50]"
      footerClassName="flex !flex-row !justify-center !items-center px-3 !py-1 !gap-2"
      size="md"
      buttons={[
        {
          label: 'SAVE',
          bgClass: 'bg-SeabiscuitMainThemeColor',
          textClass: 'text-white ',
          fullWidth: false,
          loading,
          onClick: handleSubmit(isHorse ? updateHorse : updateUser),
        },
        {
          label: 'CANCEL',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          className: 'outline-none',
          fullWidth: false,
          loading,
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor ',
          onClick: () => handleModal(false, MODAL_CONSTS.UPDATE_USER_NUMBER),
        },
      ]}
    >
      <div className=" p-4 overflow-visible  flex justify-center">
        <BaseNumberCard
          icon={numberLogo || '/assets/images/logos/custom-number-logo.png'}
          label={fieldName || ''}
          saved={false}
          standaloneCard={true}
          onImageUpload={handleImageUpload}
          onImageRemove={handleImageRemove}
          imageUrl={imageUrl}
          isLoading={loading}
          numberField={register(isHorse ? fieldKey : `${fieldKey}.documentNumber`)}
          control={control}
          dateFieldName={isHorse ? `${cutValue}Expiration` : `${fieldKey}.endDate`}
          numberPlaceholder="Enter number..."
          showImageInput={fieldType !== 'tracking-number'}
        />
      </div>
    </MainModal>
  )
}
